import sortBy from 'lodash/sortBy';
import { Fragment, PropsWithChildren } from 'react';

import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import { Markdown } from 'components/Markdown';
import { useHealthInsuranceContext } from 'contexts/HealthInsuranceContext';
import {
  AddIcon,
  Form,
  Icon,
  QuestionsContextReturnType,
  formModelGet,
  getStringUuid,
  useExtendedReducer
} from '@portal/ui-library';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { PartnerIdProp, RecursiveKeyOf } from '@portal/ui-library/dist/models';
import { partnerField } from 'models';
import {
  HealthQuestionDetail,
  Partner,
  Question,
  QuestionTypeEnum
} from 'models/extension-generated';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { QuestionDetails } from './QuestionDetails';
import { Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';

const DarkTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#3B3B3B',
    maxWidth: 500,
    padding: 12
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#3B3B3B'
  }
});

type QuestionsAction = Omit<
  QuestionsContextReturnType,
  'fetchQuestions' | 'questions' | 'isLoading'
>;

const { Input, YesNo } = Form.Components;

type QuestionWrapperProps = PropsWithChildren & {
  colClassName?: string;
  inputClassName?: string;
  placeholder?: string;
  hasUnit?: boolean;
  isDisabled?: boolean;
  onChange?: (value: unknown) => void;
};

function renderClosedQuestion(
  partnerId: string,
  key: string,
  action: QuestionsAction,
  tooltip?: string,
  hint?: string,
  props?: QuestionWrapperProps,
  headline?: string
) {
  return (
    <div
      className={classNames({
        'grid-my-res': !headline,
        'mb-[24px] m:mb-[32px]': headline
      })}
    >
      <div className="pb-[15px] flex items-center">
        <Typography variant="bodyMDRegular">{action.getQuestionText(key, false)}</Typography>

        {tooltip && (
          <DarkTooltip
            title={
              <div className="w-[20vw]" id={`${key}-tooltip`}>
                <Typography variant="bodySMRegular">
                  <Markdown markdown={tooltip} />
                </Typography>
              </div>
            }
            placement="bottom-start"
          >
            <IconButton
              size="small"
              className="text-highlight-80"
              aria-describedby={`${key}-tooltip`}
            >
              <InfoIcon />
            </IconButton>
          </DarkTooltip>
        )}
      </div>

      <YesNo
        question={action.getQuestionText(key, false)}
        hideLabel
        ariaDescribedby={`${key}-hint ${key}-effective-hint`}
        name={action.getQuestionText(key, false)}
        componentKey={action.getQuestionKey(partnerId, key)}
        onChange={(value) => {
          props?.onChange?.(value.value);
        }}
        disabled={props?.isDisabled}
      />
      {hint && (
        <div
          id={`${key}-hint`}
          className="my-s px-s bg-surface-60 gap-s flex items-center rounded-md"
        >
          <InfoIcon />
          <Typography variant="bodySMRegular" className="inline">
            <Markdown markdown={hint} />
          </Typography>
        </div>
      )}
    </div>
  );
}

function renderInput(
  partnerId: string,
  key: string,
  action: QuestionsAction,
  tooltip?: string,
  props?: QuestionWrapperProps,
  headline?: string
) {
  return (
    <div
      className={classNames({
        'grid-my-res': !headline,
        'mb-[24px] m:mb-[32px]': headline
      })}
    >
      <div className="pb-[15px]">
        <Typography variant="bodyMDRegular">{action.getQuestionText(key, false)}</Typography>
        {tooltip && (
          <DarkTooltip
            title={
              <div id={`${key}-tooltip`}>
                <Typography variant="bodySMRegular">
                  <ReactMarkdown>{`${tooltip}`}</ReactMarkdown>
                </Typography>
              </div>
            }
            placement="bottom-start"
          >
            <Button variant="text" className="p-0" aria-describedby={`${key}-tooltip`}>
              <InfoIcon />
            </Button>
          </DarkTooltip>
        )}
      </div>
      <Input
        componentKey={action.getQuestionKey(partnerId, key)}
        label={action.getQuestionText(key, false)}
        ariaDescribedby={`${key}-hint ${key}-effective-hint`}
        disabled={props?.isDisabled}
        unitText={action.getQuestionUnit(key)}
        type="number"
        // innerText={getQuestionUnit(key)}
        // maxValue={getQuestionMaxValue(key)}
      />
    </div>
  );
}

type QuestionsProps = PartnerIdProp & { action: QuestionsAction } & {
  questions: Question[];
  /**
   * Whether there is loading event in progress.
   * This variable is true in following cases:
   *  - application load
   *  - initial configuration load
   */
  isLoading: boolean;
  headlineId?: string;
};

export const getSubQuestionsField = (partnerId: string, questionId: string): string => {
  return partnerField(
    partnerId,
    `applicationInformation.health[${questionId}].details` as RecursiveKeyOf<Partner>
  );
};

/* common questions formatter */
const Questions = ({ partnerId, questions, action }: QuestionsProps) => {
  const { t } = useTranslation('wizardHealth');
  const { state, dispatch } = useHealthInsuranceContext();
  const { addDetail, removeQuestionDetails, removeDetail } = useExtendedReducer(dispatch);

  return (
    <>
      {questions.map((question) => {
        const { id: questionId } = question;
        const mainQuestionKey = action.getQuestionKey(partnerId, question.id);
        const mainQuestionValue = formModelGet(state.model, mainQuestionKey);
        const detailsKey = getSubQuestionsField(partnerId, questionId);
        const details: HealthQuestionDetail[] = sortBy(
          formModelGet(state.model, detailsKey) || [],
          'orderNo'
        );

        const handleAddDiagnosisClick = () => {
          const newId = getStringUuid();
          addDetail({
            detailId: newId,
            partnerId,
            questionId
          });
        };
        const sortedSubQuestions = sortBy(question.subQuestions || [], 'orderNo');
        return (
          <Fragment key={question.id}>
            {question.headline && (
              <div className="mb-[12px] s:mb-s">
                <Typography component={'h4'} variant="bodyMDBold">
                  {question.headline}
                </Typography>
              </div>
            )}

            {question.questionType === QuestionTypeEnum.CLOSED_QUESTION &&
              renderClosedQuestion(
                partnerId,
                question.id,
                action,
                question.tooltip,
                question.hint,
                {
                  onChange: (value) => {
                    if (value === true && sortedSubQuestions.length > 0) {
                      addDetail({
                        detailId: getStringUuid(),
                        partnerId,
                        questionId
                      });
                    } else {
                      removeQuestionDetails({
                        partnerId,
                        questionId
                      });
                    }
                  }
                },
                question.headline
              )}
            {question.questionType === QuestionTypeEnum.INT_QUESTION &&
              renderInput(
                partnerId,
                question.id,
                action,
                question.hint,
                undefined,
                question.headline
              )}

            {question.effectiveHint && mainQuestionValue === true && (
              <div
                className="my-s gap-[8px] flex text-danger-60 items-center rounded-sm"
                id={`${question.id}-effective-hint`}
                aria-live="assertive"
              >
                <InfoIcon />
                <Typography variant="bodySMRegular" className="inline">
                  <Markdown markdown={question.effectiveHint} />
                </Typography>
              </div>
            )}
            <div
              className={classNames('space-y-6', {
                'mt-l': mainQuestionValue && sortedSubQuestions.length > 0
              })}
            >
              <>
                {/* Render existing question details */}
                {details.map((detail, detailIndex) => {
                  return (
                    <div key={detail.id} className="space-y-4">
                      <div className="flex items-center gap-[8px]">
                        <Typography variant="bodyMDBold">{t('section.details.title')}</Typography>
                        {details.length > 1 && (
                          <Button
                            variant="outlined"
                            className="underline"
                            onClick={() => {
                              removeDetail({
                                partnerId,
                                questionId,
                                detailId: detail.id || ''
                              });
                            }}
                          >
                            {t('section.details.deleteAction')}
                          </Button>
                        )}
                      </div>
                      {/* Map all subquestions for this detail */}
                      {sortedSubQuestions.map((subQuestion, index) => {
                        return (
                          <QuestionDetails
                            detailIndex={detailIndex}
                            key={`${subQuestion.id}-${index}`}
                            detailId={detail.id || ''}
                            partnerId={partnerId}
                            questionId={questionId}
                            subQuestion={subQuestion}
                            question={question}
                            mainQuestionValue={mainQuestionValue}
                          />
                        );
                      })}
                      <div className="pb-m">
                        <Button variant="outlined" onClick={handleAddDiagnosisClick}>
                          <Icon icon={<AddIcon />} className="mr-s" />
                          {t('section.details.addDetailAction')}
                        </Button>
                      </div>
                      <hr className="h-px mb-s border-0 bg-surface-20" />
                    </div>
                  );
                })}
              </>
            </div>
          </Fragment>
        );
      })}
    </>
  );
};

export default Questions;
